import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Link } from "react-router-dom";
import { Download, Clock, AlertCircle, Ban, Check, Upload, Image, FileText as FileIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: "Pendiente", reviewing: "En revisión", approved: "Aprobada", rejected: "Rechazada"
};

function isExpired(q) {
  if (!q.expires_at) return false;
  return new Date() > new Date(q.expires_at);
}
function daysLeft(q) {
  if (!q.expires_at) return null;
  const diff = new Date(q.expires_at) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}
function hasUnpricedCustom(q) {
  return (q.items || []).some(i => i.is_custom && (i.unit_price === 0 || !i.unit_price)) && q.status === "pending";
}

function getQuoteState(q) {
  if (q.status === "rejected") return "rejected";
  if (isExpired(q) && q.status !== "approved") return "expired";
  if (q.status === "pending") return "pending";
  if (q.status === "reviewing") return "reviewing";
  if (q.status === "approved") {
    if (!q.client_accepted) return "awaiting_client";
    if (q.requires_advance && !q.payment_verified) {
      if (q.payment_proof_file_id) return "payment_uploaded";
      return "awaiting_payment";
    }
    if (q.payment_verified || !q.requires_advance) return "ready_production";
  }
  return q.status;
}

const STATE_BADGE = {
  pending: { label: "Pendiente", cls: "bg-[#F1F5F9] text-[#64748B]" },
  reviewing: { label: "En revisión", cls: "bg-[#FEF3C7] text-[#92400E]" },
  awaiting_client: { label: "Esperando tu aprobación", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
  awaiting_payment: { label: "Pendiente de pago", cls: "bg-[#FEF3C7] text-[#92400E]" },
  payment_uploaded: { label: "Comprobante enviado", cls: "bg-[#E0E7FF] text-[#3730A3]" },
  ready_production: { label: "En producción", cls: "bg-[#D1FAE5] text-[#065F46]" },
  rejected: { label: "Rechazada", cls: "bg-[#FEE2E2] text-[#991B1B]" },
  expired: { label: "Vencida", cls: "bg-[#FEE2E2] text-[#991B1B]" },
};

export default function ClientQuotations() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchQuotes = () => {
    API.get("/quotations").then((r) => { setQuotes(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchQuotes(); }, []);

  const downloadPdf = async (q) => {
    if (hasUnpricedCustom(q)) {
      toast.error("Tu cotización aún está siendo revisada por nuestro equipo.");
      return;
    }
    try {
      const response = await API.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `cotizacion-${q.id.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Error al descargar PDF");
    }
  };

  const acceptQuote = async (q) => {
    try {
      await API.post(`/quotations/${q.id}/accept`);
      toast.success("Cotización aceptada");
      fetchQuotes();
      setSelectedQuote(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al aceptar");
    }
  };

  const uploadPayment = async (q, file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await API.post(`/quotations/${q.id}/upload-payment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comprobante enviado correctamente");
      fetchQuotes();
      setSelectedQuote(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir comprobante");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="client-quotations-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Mi Portal</p>
            <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Mis Cotizaciones</h1>
          </div>
          <Link to="/cotizar">
            <Button className="bg-[#E30613] text-white font-bold rounded-sm px-5 py-2.5 hover:bg-[#C80410] text-sm" data-testid="new-quote-btn">Nueva Cotización</Button>
          </Link>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-sm">
          {loading ? (
            <p className="px-5 py-12 text-sm text-[#94A3B8] text-center">Cargando...</p>
          ) : quotes.length === 0 ? (
            <p className="px-5 py-12 text-sm text-[#94A3B8] text-center">No tienes cotizaciones. ¡Crea tu primera!</p>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {quotes.map((q) => {
                const state = getQuoteState(q);
                const badge = STATE_BADGE[state] || STATE_BADGE.pending;
                const total = q.adjusted_total || q.total;
                const advAmt = q.requires_advance ? (total * (q.advance_percent || 50) / 100) : 0;
                const remaining = total - advAmt;
                const expired = isExpired(q);
                const days = daysLeft(q);
                const unpriced = hasUnpricedCustom(q);

                return (
                  <div key={q.id} className={`px-5 py-4 ${expired && q.status !== "approved" ? "opacity-50" : ""}`} data-testid={`quote-row-${q.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-[#0F172A]">{q.items?.map((i) => i.product_name).join(", ")}</p>
                        <p className="text-xs text-[#475569] mt-1">
                          {q.items?.[0]?.quantity} pzas · {q.items?.[0]?.technique_name} · {new Date(q.created_at).toLocaleDateString()}
                        </p>
                        {q.admin_notes && <p className="text-xs text-[#0047AB] mt-1">PYM: {q.admin_notes}</p>}
                        {q.confirmed_delivery_date && <p className="text-xs text-[#10B981] mt-1">Entrega confirmada: {q.confirmed_delivery_date}</p>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {days !== null && days <= 3 && !expired && q.status !== "approved" && (
                          <span className="text-[10px] text-amber-600 font-medium flex items-center gap-1"><Clock className="w-3 h-3" />{days}d</span>
                        )}
                        {!unpriced && !expired && (
                          <button onClick={() => downloadPdf(q)} className="text-[#0047AB] hover:text-[#003A8C] p-1" title="PDF" data-testid={`download-pdf-${q.id}`}>
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        <span className={`${badge.cls} px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm whitespace-nowrap`}>{badge.label}</span>
                        <span className="text-base font-bold text-[#0F172A]">Q{total}</span>
                      </div>
                    </div>

                    {/* Advance info */}
                    {q.requires_advance && q.status !== "rejected" && (
                      <div className="mt-2 bg-[#F0F4FF] border border-[#0047AB]/10 rounded-sm px-3 py-2 flex items-center gap-4 text-xs">
                        <span className="text-[#0047AB] font-medium">Anticipo {q.advance_percent || 50}%: Q{advAmt.toFixed(2)}</span>
                        <span className="text-[#475569]">Resta: Q{remaining.toFixed(2)}</span>
                        {q.payment_verified && <span className="text-green-600 font-medium flex items-center gap-1"><Check className="w-3 h-3" />Pago verificado</span>}
                        {q.payment_proof_file_id && !q.payment_verified && <span className="text-[#6366F1] font-medium">Comprobante enviado - En verificación</span>}
                      </div>
                    )}

                    {/* Action buttons based on state */}
                    {state === "awaiting_client" && (
                      <div className="mt-3 bg-[#DBEAFE] border border-[#0047AB]/20 rounded-sm p-3">
                        <p className="text-sm text-[#1E40AF] font-medium mb-2">Tu cotización fue aprobada por PYM Promocionales</p>
                        <p className="text-xs text-[#475569] mb-3">Revisa los detalles, precios y condiciones. Al aprobar, confirmas tu acuerdo con esta cotización.</p>
                        <div className="flex gap-2">
                          <Button onClick={() => setSelectedQuote(q)} className="bg-[#0047AB] text-white font-bold rounded-sm text-sm hover:bg-[#003A8C]" data-testid={`view-details-${q.id}`}>
                            Ver detalle y aprobar
                          </Button>
                        </div>
                      </div>
                    )}

                    {state === "awaiting_payment" && (
                      <div className="mt-3 bg-[#FEF3C7] border border-amber-300/30 rounded-sm p-3">
                        <p className="text-sm text-[#92400E] font-medium mb-1">Cotización aceptada - Pendiente de pago</p>
                        <p className="text-xs text-[#475569] mb-3">Realiza el depósito o transferencia y sube tu comprobante. La producción inicia al verificar el pago.</p>
                        <label className="inline-flex items-center gap-2 bg-[#E30613] text-white font-bold rounded-sm px-4 py-2 text-sm hover:bg-[#C80410] cursor-pointer transition-colors" data-testid={`upload-payment-${q.id}`}>
                          <Upload className="w-4 h-4" /> Subir comprobante de pago
                          <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => e.target.files?.[0] && uploadPayment(q, e.target.files[0])} disabled={uploading} />
                        </label>
                      </div>
                    )}

                    {state === "payment_uploaded" && (
                      <div className="mt-3 bg-[#E0E7FF] border border-[#6366F1]/20 rounded-sm p-3 flex items-center gap-3">
                        <Image className="w-5 h-5 text-[#6366F1]" />
                        <div>
                          <p className="text-sm text-[#3730A3] font-medium">Comprobante enviado: {q.payment_proof_filename}</p>
                          <p className="text-xs text-[#475569]">Estamos verificando tu pago. Te notificaremos pronto.</p>
                        </div>
                      </div>
                    )}

                    {state === "ready_production" && (
                      <div className="mt-2 bg-[#D1FAE5] border border-green-300/30 rounded-sm px-3 py-2 flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-green-800 font-medium">Pago verificado - Tu pedido está en producción</span>
                      </div>
                    )}

                    {expired && q.status === "pending" && (
                      <div className="mt-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2 flex items-center justify-between">
                        <span className="text-xs text-red-700">Cotización vencida (10 días). Solicita una nueva.</span>
                        <Link to="/cotizar" className="text-xs text-[#0047AB] font-medium hover:underline">Cotizar de nuevo</Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quote detail & accept dialog */}
      <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo' }}>Detalle de Cotización</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              {/* Items */}
              <div className="border border-[#E2E8F0] rounded-sm divide-y divide-[#E2E8F0]">
                {selectedQuote.items?.map((item, i) => (
                  <div key={i} className="px-4 py-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-[#0F172A]">{item.product_name}</span>
                      <span className="text-sm font-bold text-[#0F172A]">Q{(item.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-[#475569] mt-1">{item.technique_name} · {item.quantity} pzas · Q{(item.unit_price || 0).toFixed(2)} c/u</p>
                    {item.custom_description && <p className="text-xs text-[#0047AB] mt-1">{item.custom_description}</p>}
                  </div>
                ))}
                <div className="px-4 py-3 bg-[#F8FAFC]">
                  <div className="flex justify-between pt-1">
                    <span className="text-sm font-bold text-[#0F172A]">Total</span>
                    <span className="text-lg font-bold text-[#E30613]">Q{(selectedQuote.adjusted_total || selectedQuote.total || 0).toFixed(2)}</span>
                  </div>
                  <p className="text-[10px] text-[#94A3B8] mt-1">Precios incluyen IVA e ISR</p>
                </div>
              </div>

              {/* Conditions */}
              {selectedQuote.confirmed_delivery_date && (
                <div className="text-sm"><span className="font-medium">Entrega confirmada:</span> {selectedQuote.confirmed_delivery_date}</div>
              )}
              {selectedQuote.admin_notes && (
                <div className="bg-[#F0F4FF] border border-[#0047AB]/10 rounded-sm p-3">
                  <p className="text-xs font-bold text-[#0047AB] uppercase mb-1">Notas de PYM</p>
                  <p className="text-sm text-[#0F172A]">{selectedQuote.admin_notes}</p>
                </div>
              )}
              {selectedQuote.requires_advance && (
                <div className="bg-[#FEF3C7] border border-amber-200 rounded-sm p-3">
                  <p className="text-xs font-bold text-[#92400E] uppercase mb-1">Anticipo Requerido</p>
                  <p className="text-sm text-[#0F172A]">
                    {selectedQuote.advance_percent}% = Q{((selectedQuote.adjusted_total || selectedQuote.total) * (selectedQuote.advance_percent || 50) / 100).toFixed(2)}
                  </p>
                  <p className="text-xs text-[#475569] mt-1">El tiempo de entrega inicia al recibir el anticipo por depósito o transferencia bancaria.</p>
                </div>
              )}

              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm p-3">
                <p className="text-xs text-[#475569]">Al aprobar esta cotización confirmas tu acuerdo con los precios, condiciones y tiempos de entrega indicados. Esta cotización tiene vigencia de 10 días.</p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={() => acceptQuote(selectedQuote)} className="flex-1 bg-[#10B981] text-white font-bold rounded-sm hover:bg-[#059669]" data-testid="accept-quote-btn">
                  <Check className="w-4 h-4 mr-1" /> Aprobar Cotización
                </Button>
                <Button onClick={() => setSelectedQuote(null)} variant="outline" className="border-[#E2E8F0] text-[#475569] rounded-sm" data-testid="cancel-accept-btn">
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
